import React from "react";
import { connect } from "react-redux";
import language from "../../language";
import { Table } from "../Table";
import { Modal, DeleteModal } from "../Modal";
import { CSVLink } from "react-csv";
import exportPDF from "../../utils/exportPDF";
import NewTestAssayRule from "./NewTestAssayRule";
import {
  deleteTestAssayRule,
  fetchTestAssayRules,
} from "../../utils/queries/testAssayRuleQuery";
import icons from "../../constants/icons";
import { defineRole } from "../../utils/handleAccessRoles";
import { getStorage } from "../../utils/storage";
import { ROLE_TEST_ASSAY_RULE } from "../../constants/ROLES";
import { DashboardHeader } from "../Header";

// eslint-disable-next-line no-unused-vars
let copyData = [];

class TestAssayRules extends React.Component {
  csvDownload = React.createRef();

  state = {
    isLoading: false,
    data: [],
    page: 1,
    limit: 10,
    search_text: "",
    selectedData: {},
    delete_value: "",
    error: {},
    csvData: [],
    user: {},
  };

  componentDidMount = async () => {
    const user = await getStorage();
    this.setState({ user });
    await this.getData(true);
  };

  onChangeText(field, e) {
    let { error } = this.state;
    delete error[field];
    this.setState({
      [field]: e?.target?.value,
      error,
    });
  }

  getData = async (isLoading, search = {}) => {
    try {
      const { page, limit } = this.state;
      const { defaultLanguage } = this.props;

      this.setState({ isLoading, filters: { ...search } });

      const { data, count, totalRecords } = await fetchTestAssayRules(
        defaultLanguage,
        {
          page,
          limit,
          ...search,
        },
      );

      this.setState({
        data: data,
        totalPageCount: count,
        totalRecords,
        isLoading: false,
        totalRecords,
      });

      copyData = data.slice(0);
    } catch (error) {
      console.log(error);
      this.setState({ isLoading: false });
    }
  };

  handlePagination(page, _tab, limit) {
    this.setState(
      {
        page,
        ...(limit != null && { limit }),
      },
      () => {
        this.getData(true, this.state.filters);
      }
    );
  }

  handleShowModal(modal, modalTitle, selectedData = {}) {
    this.setState({
      [modal]: true,
      modalTitle,
      selectedData,
    });
  }

  handleCloseModal(modal) {
    this.setState({ [modal]: false });
  }

  returnTableHeaders() {
    return [
      {
        title: language[this.props.defaultLanguage].code,
        key: "code",
        styles: { textTransform: "uppercase" },
      },
      {
        title: language[this.props.defaultLanguage].name,
        key: "name",
        styles: { textTransform: "uppercase" },
      },
      {
        title: language[this.props.defaultLanguage].disease,
        key: "disease",
      },
      {
        title: language[this.props.defaultLanguage].test_type,
        key: "testType",
        render: (val) =>
          val === "bloodGrouping" ? "Blood Grouping" : "Serology",
      },
      {
        title: language[this.props.defaultLanguage].assay_role,
        key: "assayRole",
      },
      {
        title: language[this.props.defaultLanguage].stage,
        key: "stage",
      },
      {
        title: language[this.props.defaultLanguage].block_donor,
        key: "blockDonor",
        render: (val) => (val ? "Yes" : "No"),
      },
      {
        title: language[this.props.defaultLanguage].block_donation,
        key: "blockDonation",
        render: (val) => (val ? "Yes" : "No"),
      },
      {
        title: language[this.props.defaultLanguage].result_condition,
        key: "resultCondition",
        render: (val) => {
          if (!val && val !== 0) return "-";
          const s = typeof val === "object" ? (val.name || val.value || val.label || "") : String(val);
          if (!s) return "-";
          return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");
        },
      },
      {
        title: language[this.props.defaultLanguage].donation_status,
        key: "donationStatus",
        render: (val) => {
          if (!val) return "-";
          const s = typeof val === "object" ? (val.name || val.value || val.label || "") : String(val);
          return s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "-";
        },
      },
      {
        title: language[this.props.defaultLanguage].donor_status,
        key: "donorStatus",
        render: (val) => {
          if (!val) return "-";
          const s = typeof val === "object" ? (val.name || val.value || val.label || "") : String(val);
          return s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "-";
        },
      },
      {
        title: language[this.props.defaultLanguage].mandatory_actions,
        key: "mandatoryActions",
        render: (val) => (val === true ? "Yes" : val === false ? "No" : "-"),
      },
      {
        title: language[this.props.defaultLanguage].is_active,
        key: "isActive",
        render: (val) => (val ? "Active" : "Inactive"),
      },
      {
        title: language[this.props.defaultLanguage].created_at,
        key: "createdAt",
        isMoment: true,
        formatTime: "lll",
      },
      {
        title: language[this.props.defaultLanguage].actions,
        key: "action",
      },
    ];
  }

  onDelete = async () => {
    try {
      this.setState({ isDeleting: true });

      await deleteTestAssayRule(
        this.props.defaultLanguage,
        this.state.selectedData._id,
      );

      this.setState({ isDeleting: false });
      this.getData(true);
      this.handleCloseModal("showDeleteModal");
    } catch (error) {
      this.setState({ isDeleting: false });
    }
  };

  downloadExcel = async () => {
    try {
      this.setState({ isLoading: true });

      const { page, limit } = this.state;
      const { defaultLanguage } = this.props;

      const { data } = await fetchTestAssayRules(defaultLanguage, {
        page,
        limit,
      });

      this.setState({ isLoading: false, csvData: data });
      this.csvDownload.current?.link.click();
    } catch (error) {
      this.setState({ isLoading: false });
    }
  };

  downloadPDF = async () => {
    this.setState({ isLoading: true });

    const { page, limit } = this.state;
    const { defaultLanguage } = this.props;

    const headers = this.returnTableHeaders();
    const { data } = await fetchTestAssayRules(defaultLanguage, {
      page,
      limit,
    });

    this.setState({ isLoading: false });
    exportPDF("Test Assay Rules", headers, data);
  };

  handleSearch = async (e) => {
    const search = e?.target?.value;
    this.setState({ search_text: search });
  };

  handleRemoteSearch = async (search) => {
    this.setState({ searching: true, page: 1 });

    await this.getData(true, {
      q: search || search !== "" ? search : undefined,
      page: 1,
      limit: 10,
    });

    this.setState({ searching: false });
  };

  render() {
    const { export_, edit, delete_ } = language[this.props.defaultLanguage];

    return (
      <>
        <DashboardHeader
          title={language[this.props.defaultLanguage].test_assay_rules}
        />
        <Table
          data={this.state.data}
          isSearch
          page={this.state.page}
          limit={this.state.limit}
          search_text={this.state.search_text}
          addSearchButton
          searching={this.state.searching}
          handleSearch={this.handleSearch.bind(this)}
          handlePressSearch={() =>
            this.handleRemoteSearch(this.state.search_text)
          }
          totalPageCount={this.state.totalPageCount}
         totalRecords={this.state.totalRecords}
          totalRecords={this.state.totalRecords}
          isLoading={this.state.isLoading}
          handlePagination={this.handlePagination.bind(this)}
          headers={this.returnTableHeaders()}
          showAdd={true}
          addButtonText={language[this.props.defaultLanguage].add}
          handleAddPressed={this.handleShowModal.bind(
            this,
            "showModal",
            language[this.props.defaultLanguage].add_test_assay_rule,
          )}
          rowPress={(item) =>
            this.handleShowModal("showModal", item.name, item)
          }
          filters={[
            {
              type: "export",
              title: export_,
              button_type: "dropdown",
              icon: "bx bxs-download",
              options: [
                { name: "PDF", onPress: this.downloadPDF.bind(this) },
                { name: "CSV", onPress: this.downloadExcel.bind(this) },
              ],
            },
          ]}
          actions={[
            {
              canAccess: defineRole({
                roles: this.state.user.accessRole,
                menu: ROLE_TEST_ASSAY_RULE,
                operation: "update",
              }),
              name: edit,
              onPress: (item) =>
                this.handleShowModal(
                  "showModal",
                  language[this.props.defaultLanguage].edit,
                  item,
                ),
              icon: icons.edit,
            },
            {
              canAccess: defineRole({
                roles: this.state.user.accessRole,
                menu: ROLE_TEST_ASSAY_RULE,
                operation: "delete",
              }),
              name: delete_,
              onPress: this.handleShowModal.bind(
                this,
                "showDeleteModal",
                language[this.props.defaultLanguage].delete,
              ),
              icon: icons.delete,
            },
          ]}
        />
        <Modal
          handleClose={this.handleCloseModal.bind(this, "showModal")}
          show={this.state.showModal}
          title={this.state.modalTitle}
          showHeaderBottomBorder={false}
        >
          <NewTestAssayRule
            handleCloseModal={this.handleCloseModal.bind(this, "showModal")}
            getData={this.getData.bind(this)}
            {...this.state.selectedData}
          />
        </Modal>
        <Modal
          handleClose={this.handleCloseModal.bind(this, "showDeleteModal")}
          show={this.state.showDeleteModal}
          title={this.state.modalTitle}
        >
          <DeleteModal
            handleCloseModal={this.handleCloseModal.bind(
              this,
              "showDeleteModal",
            )}
            error={this.state.error.delete_password}
            value={this.state.delete_value}
            onDelete={this.onDelete.bind(this)}
            onChange={(e) => this.onChangeText("delete_value", e)}
            isDeleting={this.state.isDeleting}
            name={this.state.selectedData.name}
            getData={this.getData.bind(this)}
          />
        </Modal>
        <CSVLink
          ref={this.csvDownload}
          filename={"TestAssayRules" + new Date().getTime()}
          data={this.state.csvData}
        ></CSVLink>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return { defaultLanguage };
};

export default connect(mapStateToProps)(TestAssayRules);
